/**
 * bestPicks.ts
 *
 * Generates curated "Best Picks" sections from venue data.
 * Logic is transparent and data-driven, not magic.
 */

import type { Venue } from '../types'
import { isVenueActiveNow, getMinDealPrice } from './filters'
import { getVenueStatus } from './happeningNow'
import { scorevenue } from './scoring'
import type { UserLocation } from '../types'

export interface BestPicksSection {
  id: string
  title: string
  subtitle: string
  icon: string
  venues: Venue[]
  trendingIds?: string[] // ordered by view count for trending section
}

const MAX_PER_SECTION = 6
const MAX_SECTIONS_PER_VENUE = 2

// ─────────────────────────────────────────────
// SORT HELPERS
// ─────────────────────────────────────────────

function byEngagement(a: Venue, b: Venue): number {
  const aConf = (a as any).confirmations ?? 0
  const bConf = (b as any).confirmations ?? 0
  if (bConf !== aConf) return bConf - aConf
  if (b.upvote_count !== a.upvote_count) return b.upvote_count - a.upvote_count
  return a.name.localeCompare(b.name)
}

function byPriceForType(type: string) {
  return (a: Venue, b: Venue): number => {
    const aPrice = getMinPriceForType(a, type)
    const bPrice = getMinPriceForType(b, type)
    if (aPrice !== null && bPrice !== null) return aPrice - bPrice
    if (aPrice !== null) return -1
    if (bPrice !== null) return 1
    return byEngagement(a, b)
  }
}

// ─────────────────────────────────────────────
// MAIN BUILDER
// ─────────────────────────────────────────────

export function buildBestPicksSections(
  venues: Venue[],
  userLocation?: UserLocation | null,
  trendingIds?: string[]  // ordered venue IDs from venue_impressions — most viewed first
): BestPicksSection[] {

  const appearances = new Map<string, number>()
  function canAppear(venue: Venue, exempt = false): boolean {
    if (exempt) return true
    return (appearances.get(venue.id) ?? 0) < MAX_SECTIONS_PER_VENUE
  }
  function record(venue: Venue) {
    appearances.set(venue.id, (appearances.get(venue.id) ?? 0) + 1)
  }

  const sections: BestPicksSection[] = []

  // ── 1. HAPPENING NOW (exempt from dedup) ─────────────────────────────
  const happeningNow = venues
    .filter(v => isVenueActiveNow(v))
    .sort((a, b) => scorevenue(b) - scorevenue(a))
    .slice(0, MAX_PER_SECTION)

  if (happeningNow.length > 0) {
    happeningNow.forEach(record)
    sections.push({
      id: 'happening_now',
      title: 'Happening Now',
      subtitle: 'Happy hours active right now',
      icon: '🟢',
      venues: happeningNow,
    })
  }

  // ── 2. TRENDING TONIGHT ──────────────────────────────────────────────
  // Ordered by real view count from venue_impressions (last 24h).
  // Falls back gracefully if no trending data yet.
  if (trendingIds && trendingIds.length > 0) {
    const venueMap = new Map(venues.map(v => [v.id, v]))
    const trending = trendingIds
      .map(id => venueMap.get(id))
      .filter((v): v is Venue => v != null && canAppear(v, true))
      .slice(0, MAX_PER_SECTION)

    if (trending.length >= 2) {
      trending.forEach(record)
      sections.push({
        id: 'trending_tonight',
        title: 'Trending Tonight',
        subtitle: 'Most viewed by Happy Hour Unlocked users today',
        icon: '🔥',
        venues: trending,
        trendingIds,
      })
    }
  }

  // ── 3. STARTING SOON (exempt from dedup) ─────────────────────────────
  const startingSoon = venues
    .map(v => ({ venue: v, status: getVenueStatus(v) }))
    .filter(({ status }) =>
      status.status === 'starts_soon' ||
      (status.status === 'later_today' && (status.minutesUntil ?? 999) <= 90)
    )
    .sort((a, b) => (a.status.minutesUntil ?? 999) - (b.status.minutesUntil ?? 999))
    .map(({ venue }) => venue)
    .slice(0, MAX_PER_SECTION)

  if (startingSoon.length > 0) {
    startingSoon.forEach(record)
    sections.push({
      id: 'starting_soon',
      title: 'Starting Soon',
      subtitle: 'Happy hours kicking off in the next 90 minutes',
      icon: '⏰',
      venues: startingSoon,
    })
  }

  // ── 4. CHEAPEST DRINKS ───────────────────────────────────────────────
  const cheapest = venues
    .filter(v => canAppear(v))
    .map(v => ({ venue: v, price: getMinDealPrice(v) }))
    .filter(({ price }) => price !== null && price <= 6)
    .sort((a, b) => {
      if (a.price !== b.price) return (a.price ?? 99) - (b.price ?? 99)
      return byEngagement(a.venue, b.venue)
    })
    .map(({ venue }) => venue)
    .slice(0, MAX_PER_SECTION)

  if (cheapest.length >= 2) {
    cheapest.forEach(record)
    sections.push({
      id: 'cheapest_drinks',
      title: 'Cheapest Drinks',
      subtitle: 'Best bang for your buck — deals under $6',
      icon: '💰',
      venues: cheapest,
    })
  }

  // ── 5. BEST COCKTAIL DEALS ───────────────────────────────────────────
  const cocktailVenues = venues
    .filter(v =>
      canAppear(v) &&
      (v.schedules ?? []).some(s => s.deals.some(d => d.type === 'cocktail'))
    )
    .sort(byPriceForType('cocktail'))
    .slice(0, MAX_PER_SECTION)

  if (cocktailVenues.length >= 2) {
    cocktailVenues.forEach(record)
    sections.push({
      id: 'cocktail_deals',
      title: 'Best Cocktail Deals',
      subtitle: 'Craft and classic cocktails at happy hour prices',
      icon: '🍸',
      venues: cocktailVenues,
    })
  }

  // ── 6. BEST FOOD DEALS ───────────────────────────────────────────────
  const foodVenues = venues
    .filter(v =>
      canAppear(v) &&
      (v.schedules ?? []).some(s => s.deals.some(d => d.type === 'food'))
    )
    .sort(byPriceForType('food'))
    .slice(0, MAX_PER_SECTION)

  if (foodVenues.length >= 2) {
    foodVenues.forEach(record)
    sections.push({
      id: 'food_deals',
      title: 'Best Food Deals',
      subtitle: 'Half-off apps, bites, and more',
      icon: '🍔',
      venues: foodVenues,
    })
  }

  // ── 7. ROOFTOP PICKS ─────────────────────────────────────────────────
  const rooftops = venues
    .filter(v => canAppear(v) && v.categories.includes('rooftop'))
    .sort(byEngagement)
    .slice(0, MAX_PER_SECTION)

  if (rooftops.length >= 1) {
    rooftops.forEach(record)
    sections.push({
      id: 'rooftop_picks',
      title: 'Rooftop Bars',
      subtitle: 'Drinks with a view',
      icon: '🏙️',
      venues: rooftops,
    })
  }

  // ── 8. PATIO PICKS ───────────────────────────────────────────────────
  const patioVenues = venues
    .filter(v =>
      canAppear(v) &&
      (v.categories.includes('patio') || (v as any).has_patio === true)
    )
    .sort(byEngagement)
    .slice(0, MAX_PER_SECTION)

  if (patioVenues.length >= 1) {
    patioVenues.forEach(record)
    sections.push({
      id: 'patio_picks',
      title: 'Patio Season',
      subtitle: 'Happy hour with outdoor seating',
      icon: '☀️',
      venues: patioVenues,
    })
  }

  // ── 9. DATE NIGHT PICKS ──────────────────────────────────────────────
  const dateNight = venues
    .filter(v =>
      canAppear(v) &&
      (
        v.categories.includes('date_night') ||
        v.categories.includes('cocktail_bar') ||
        v.categories.includes('wine_bar')
      )
    )
    .sort(byEngagement)
    .slice(0, MAX_PER_SECTION)

  if (dateNight.length >= 2) {
    dateNight.forEach(record)
    sections.push({
      id: 'date_night',
      title: 'Date Night Picks',
      subtitle: 'Romantic spots with great happy hour deals',
      icon: '🥂',
      venues: dateNight,
    })
  }

  return sections
}

// ─────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────

function getMinPriceForType(venue: Venue, type: string): number | null {
  const prices = (venue.schedules ?? [])
    .flatMap(s => s.deals)
    .filter(d => d.type === type && d.price != null)
    .map(d => d.price!)
  return prices.length ? Math.min(...prices) : null
}
